import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { ConversionFeature } from "@redotech/redo-conversion-settings-common/checkout-optimization-settings";
import { isIntentTrialExpiredUnpaid } from "@redotech/redo-conversion-settings-common/predictive-conversion-settings";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";

import { createContext, memo, useMemo } from "react";
import { ScheduleDemoProvider } from "../conversion/common/schedule-demo-provider";

/**
 * How locked features present in the merchant sidebar. Under $3k estimated
 * deal size → hide locked items; at/above $3k → lock icon + schedule-a-call.
 */
export type ConversionLockPresentation = "hidden" | "locked-cta";

/**
 * Coverage price assumed for the deal-size heuristic (per CRO-1430). Flat
 * 30¢/order, independent of the team's actual configured coverage price.
 */
export const COVERAGE_PRICE_CENTS = 30;

/**
 * Estimated annual revenue (in cents) below $3,000: annualOrders × 30¢.
 * Below this, locked features are hidden rather than shown with a lock CTA.
 */
export const SMALL_DEAL_SIZE_CENTS = 300_000;

/** @deprecated Use SMALL_DEAL_SIZE_CENTS */
export const INTENT_ONLY_SMALL_DEAL_SIZE_CENTS = SMALL_DEAL_SIZE_CENTS;

/**
 * Merchant-app locks from the stored admin list only. Empty / absent = nothing
 * locked. Does not invent locks from predictiveConversion or chop toggles —
 * those flags control product serving / suite enablement, not the lock list.
 */
export function deriveEffectiveLockedFeatures(
  storedLockedFeatures: readonly ConversionFeature[] | null | undefined,
): ReadonlySet<ConversionFeature> {
  if (!storedLockedFeatures || storedLockedFeatures.length === 0) {
    return new Set();
  }
  return new Set(storedLockedFeatures);
}

/**
 * Small (&lt; $3k) → hide locked features; otherwise lock-icon + schedule-a-call.
 *
 * When `annualOrders` is still loading (`null`) and `resolved` is false,
 * returns `"locked-cta"` so ≥$3k teams do not flash locked items as absent
 * (the old optimistic-"small"/hidden default). Small teams may briefly show
 * lock icons until the RPC settles.
 *
 * Callers must also pass `resolved: false` on order-count RPC failure so a
 * null annualOrders is not treated as a successful zero-order "small" team.
 */
export function deriveConversionLockPresentation(input: {
  annualOrders: number | null;
  resolved?: boolean;
}): ConversionLockPresentation {
  if (input.resolved === false) {
    return "locked-cta";
  }
  const estimatedDealSizeCents =
    (input.annualOrders ?? 0) * COVERAGE_PRICE_CENTS;
  return estimatedDealSizeCents < SMALL_DEAL_SIZE_CENTS
    ? "hidden"
    : "locked-cta";
}

export function isConversionFeatureLocked(
  feature: ConversionFeature,
  lockedFeatures: ReadonlySet<ConversionFeature>,
): boolean {
  return lockedFeatures.has(feature);
}

export interface ConversionSuiteStatuses {
  conversionEnabled: boolean;
  checkoutOptimizationEnabled: boolean;
  checkoutOutboundRatesEnabled: boolean;
  /** Intent product flag — data collection / storefront serving, not access. */
  predictiveConversionEnabled: boolean;
  /**
   * Frontend presentation locks from settings only. Empty when admin left
   * every checkbox unchecked.
   */
  lockedFeatures: ReadonlySet<ConversionFeature>;
  lockPresentation: ConversionLockPresentation;
  /**
   * False while the order-count RPC used for lock presentation is in flight.
   */
  lockPresentationResolved: boolean;
  /**
   * Secondary Conversion nav — only when checkout optimization (Conversion)
   * is enabled. Predictive alone does not open the suite; merchants without
   * Conversion land on the welcome page.
   */
  hasConversionSecondaryNav: boolean;
  /**
   * Stripped intent-style dashboard: Conversion on with a non-empty lock list
   * (partial tier / PPU). Full unlocked Conversion keeps the full dashboard.
   */
  isIntentFocusedDashboard: boolean;
  /**
   * Self-serve intent free trial ended without Shopify usage billing accepted.
   * When true the entire Conversion suite (not just Spotlights) should show
   * the IntentTrialExpired lock page. See isIntentTrialExpiredUnpaid.
   */
  intentTrialExpiredUnpaid: boolean;
}

export const ConversionSuiteStatusesContext = createContext<
  ConversionSuiteStatuses | undefined
>(undefined);

export const ConversionSuiteStatusesProvider = memo(
  function ConversionSuiteStatusesProvider({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const team = useRequiredContext(TeamContext);
    const conversionRpcClient = useRequiredContext(
      RedoConversionRpcClientContext,
    );
    const settings = team.settings;

    const checkoutOptimizationEnabled =
      !!settings.checkoutOptimization?.enabled;
    const checkoutOutboundRatesEnabled =
      !!team.shopifyIntegration?.checkoutOutboundRatesEnabled;
    const conversionEnabled =
      checkoutOptimizationEnabled && checkoutOutboundRatesEnabled;
    const predictiveConversionEnabled =
      !!settings.predictiveConversion?.enabled;

    const lockedFeatures = useMemo(
      () =>
        deriveEffectiveLockedFeatures(
          settings.checkoutOptimization?.lockedFeatures,
        ),
      [settings.checkoutOptimization?.lockedFeatures],
    );

    const shouldFetchOrderCounts = lockedFeatures.size > 0;

    const orderCountsLoad = useLoad(async () => {
      if (!shouldFetchOrderCounts) {
        return null;
      }
      return await conversionRpcClient.getOnlineStoreOrderCounts();
    }, [conversionRpcClient, shouldFetchOrderCounts]);

    const annualOrders: number | null =
      orderCountsLoad.value && orderCountsLoad.value.success
        ? orderCountsLoad.value.lastYearCount
        : null;

    const orderCountsFailed = orderCountsLoad.error !== undefined;
    const lockPresentationResolved =
      !shouldFetchOrderCounts ||
      orderCountsLoad.value !== undefined ||
      orderCountsFailed;

    // On RPC error, fail closed to locked-cta (resolved: false) rather than
    // treating null annualOrders as a successful small-deal hide.
    const lockPresentation = deriveConversionLockPresentation({
      annualOrders,
      resolved: orderCountsFailed ? false : lockPresentationResolved,
    });

    // Suite chrome requires Conversion (chop) enabled. Predictive without
    // Conversion → welcome landing; configure locks after enabling Conversion.
    const hasConversionSecondaryNav = checkoutOptimizationEnabled;

    // Partial tier: Conversion on + explicit locks (e.g. all but Spotlights).
    const isIntentFocusedDashboard =
      checkoutOptimizationEnabled && lockedFeatures.size > 0;

    const intentTrialExpiredUnpaid = isIntentTrialExpiredUnpaid({ team });

    const conversionSuiteStatuses = useMemo(
      () => ({
        conversionEnabled,
        checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
        predictiveConversionEnabled,
        lockedFeatures,
        lockPresentation,
        lockPresentationResolved,
        hasConversionSecondaryNav,
        isIntentFocusedDashboard,
        intentTrialExpiredUnpaid,
      }),
      [
        conversionEnabled,
        checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
        predictiveConversionEnabled,
        lockedFeatures,
        lockPresentation,
        lockPresentationResolved,
        hasConversionSecondaryNav,
        isIntentFocusedDashboard,
        intentTrialExpiredUnpaid,
      ],
    );

    return (
      <ConversionSuiteStatusesContext.Provider value={conversionSuiteStatuses}>
        <ScheduleDemoProvider>{children}</ScheduleDemoProvider>
      </ConversionSuiteStatusesContext.Provider>
    );
  },
);

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as POST_PURCHASE_UPSELL_ICON } from "@redotech/redo-icons/cursor-click-01.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as POST_PURCHASE_UPSELL_ICON_FILLED } from "@redotech/redo-icons/cursor-click-01_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as CONVERSION_ICON } from "@redotech/redo-icons/shopping-cart-03.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as CONVERSION_ICON_FILLED } from "@redotech/redo-icons/shopping-cart-03_filled.svg";
